import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, blockingMessage } from '../api/client';
import { qk } from '../api/queries';
import { useUserDirectory } from '../auth/UserDirectory';
import { useTenant } from '../auth/TenantProvider';
import { getDefaultItemType, type Agenda, type Item, type Subitem } from '../types/agenda';
import { generateUUID } from '../util/uuid';
import { useAutoSave } from './useAutoSave';

export type SaveStatus =
  | { type: 'saved' }
  | { type: 'saving'; pendingCount: number }
  | { type: 'offline'; pendingCount: number }
  | { type: 'conflict' }
  | { type: 'blocked' };

/** Why editing is blocked because a change failed to persist. */
export interface BlockReason {
  status?: number;
  message: string;
}

interface EditorContextValue {
  draft: Agenda;
  saveStatus: SaveStatus;
  /** Null when no item is selected. */
  selectedIndex: number | null;
  select: (i: number | null) => void;
  setMeta: (patch: Partial<Agenda>) => void;
  updateItem: (index: number, item: Item) => void;
  updateSubitems: (index: number, subitems: Subitem[]) => void;
  /** Optimistically adds item then fires POST immediately. */
  addItem: () => number;
  /** Optimistically removes item then fires DELETE immediately. */
  removeItem: (index: number) => void;
  /**
   * Optimistically removes the item from this agenda and moves it to another
   * agenda of the same meeting type. Resolves to the destination's title on
   * success, or null if the move did not complete.
   */
  moveItem: (index: number, destAgendaID: string) => Promise<string | null>;
  /** Optimistically reorders then fires PUT order immediately. */
  reorder: (from: number, to: number) => void;
  /** Replace the draft entirely (e.g. after reload). */
  resetTo: (a: Agenda) => void;
  /** Fetch fresh state from server and replace draft (for conflict resolution). */
  reloadFromServer: () => Promise<void>;
  /** Whether the conflict banner should be shown. */
  conflictBannerVisible: boolean;
  dismissConflictBanner: () => void;
  /**
   * Non-null when a save failed to persist; editing is blocked until the user
   * reloads. Set for network/5xx/auth/unexpected-4xx failures (incl. the
   * backend transaction-log write failure).
   */
  blockReason: BlockReason | null;
  editingBlocked: boolean;
  /** Non-null when there is a transient notification to show (concurrent edit, etc.). */
  notification: string | null;
  clearNotification: () => void;
  /** ID of the most recently added item; cleared after the detail panel focuses its title. */
  justAddedItemId: string | null;
  clearJustAdded: () => void;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export function useEditor(): EditorContextValue {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor must be used within EditorProvider');
  return ctx;
}

export function EditorProvider({
  initial,
  children,
}: {
  initial: Agenda;
  children: ReactNode;
}) {
  const [draft, setDraft] = useState<Agenda>(initial);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [conflictBannerVisible, setConflictBannerVisible] = useState(false);
  const [blockReason, setBlockReason] = useState<BlockReason | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [justAddedItemId, setJustAddedItemId] = useState<string | null>(null);

  // Track the server's version without triggering re-renders on every save.
  const serverVersionRef = useRef<number>(initial.version);

  const { displayName } = useUserDirectory();
  const { activeTenant } = useTenant();
  const queryClient = useQueryClient();

  // Get the meeting type config for the current agenda
  const meetingTypeConfig = useMemo(() => {
    if (!activeTenant) return undefined;
    return activeTenant.meetingTypes.find((mt) => mt.key === draft.meetingType);
  }, [draft.meetingType, activeTenant]);

  // ── Auto-save callbacks ──────────────────────────────────────────────────

  const onItemSaved = useCallback(
    (
      serverAgenda: Agenda,
      concurrentEditDetected: boolean,
      overwrotePrincipal?: string,
    ) => {
      serverVersionRef.current = serverAgenda.version;
      // Sync item timestamps (updatedAt) without replacing the whole draft,
      // so pending edits to other items are not overwritten.
      setDraft((prev) => {
        const itemById = new Map(serverAgenda.items.map((i) => [i.id, i]));
        const items = prev.items.map((localItem) => {
          const serverItem = itemById.get(localItem.id);
          return serverItem ? { ...localItem, updatedAt: serverItem.updatedAt } : localItem;
        });
        return { ...prev, version: serverAgenda.version, items };
      });
      if (concurrentEditDetected) {
        const who = displayName(overwrotePrincipal);
        setNotification(`Your change was saved, but overwrote an edit by ${who}.`);
      }
    },
    [displayName],
  );

  const onMetaSaved = useCallback((serverAgenda: Agenda) => {
    serverVersionRef.current = serverAgenda.version;
    // Update version in draft so structural ops use fresh version.
    setDraft((prev) => ({ ...prev, version: serverAgenda.version }));
  }, []);

  const onSaveError = useCallback((message: string) => {
    setNotification(`Save error: ${message}`);
  }, []);

  // Block editing when a change fails to persist. Latch the first reason so a
  // cascade of queued failures doesn't overwrite the original cause.
  const blockEditing = useCallback((error: unknown) => {
    setBlockReason((prev) =>
      prev ?? {
        status: error instanceof ApiError ? error.status : undefined,
        message: blockingMessage(error),
      },
    );
  }, []);

  const autoSave = useAutoSave({
    agendaId: initial.id,
    serverVersionRef,
    onItemSaved,
    onMetaSaved,
    onSaveError,
    onSaveBlocked: blockEditing,
  });

  const {
    pendingCount,
    isOnline,
    scheduleItemPatch,
    scheduleMetaPatch,
    cancelItemPatch,
    enqueueMutation,
    flushAll,
    recordServerItems,
    getPendingItems,
  } = autoSave;

  // Layer locally-typed-but-not-yet-sent item edits back on top of a server
  // agenda before it replaces the draft. A structural mutation's tail
  // reconciliation (add/remove/reorder) snapshots server state that predates a
  // still-debouncing item PATCH, so a wholesale replace would revert a title
  // the user just typed. Server stays authoritative for structure/order and for
  // every item without a pending edit; pending items keep their local content
  // but adopt the server `updatedAt` so the eventual PATCH baseline is correct.
  const reconcileWithPending = useCallback(
    (server: Agenda): Agenda => {
      const pending = getPendingItems();
      if (pending.size === 0) return server;
      const items = server.items.map((srv) => {
        const local = pending.get(srv.id);
        return local ? { ...local, updatedAt: srv.updatedAt } : srv;
      });
      return { ...server, items };
    },
    [getPendingItems],
  );

  // Seed lastSavedAt with the initial server state so the very first patch
  // sends an accurate `originalUpdatedAt`.
  useEffect(() => {
    recordServerItems(initial.items);
    // Only seed once per mounted agenda; downstream calls keep it in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add the insertion-point sentinel if this agenda doesn't have one yet.
  useEffect(() => {
    if (initial.items.some((i) => i.type === 'insertion-point')) return;
    const sentinel: Item = { id: generateUUID(), type: 'insertion-point', title: '' };
    const afterID = initial.items[initial.items.length - 1]?.id ?? null;
    setDraft((prev) => ({ ...prev, items: [...prev.items, sentinel] }));
    // Route the sentinel POST through the mutation queue: a user can click Add
    // the instant the editor loads while this is still in flight, and the queue
    // guarantees the sentinel exists on the server before that Add executes.
    void enqueueMutation(async (isTail) => {
      const version = serverVersionRef.current;
      try {
        const serverAgenda = await api.addItem(initial.id, sentinel, afterID, version);
        serverVersionRef.current = serverAgenda.version;
        recordServerItems(serverAgenda.items);
        if (isTail()) setDraft(reconcileWithPending(serverAgenda));
      } catch {
        setDraft((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== sentinel.id) }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush pending saves before page unload.
  useEffect(() => {
    const handler = () => flushAll();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [flushAll]);

  // ── Save status ───────────────────────────────────────────────────────────

  const saveStatus: SaveStatus = useMemo(() => {
    if (blockReason) return { type: 'blocked' };
    if (conflictBannerVisible) return { type: 'conflict' };
    if (pendingCount > 0) {
      return isOnline
        ? { type: 'saving', pendingCount }
        : { type: 'offline', pendingCount };
    }
    return { type: 'saved' };
  }, [blockReason, conflictBannerVisible, pendingCount, isOnline]);

  // ── Reload from server (conflict resolution) ───────────────────────────────

  const reloadFromServer = useCallback(async () => {
    try {
      const fresh = await api.getAgenda(initial.id);
      serverVersionRef.current = fresh.version;
      recordServerItems(fresh.items);
      setDraft(fresh);
      setConflictBannerVisible(false);
      // A successful reload re-syncs with the server, so any edit block is cleared.
      setBlockReason(null);
    } catch (e) {
      // A 401/403 means the session expired — the blocking overlay already shows
      // the right message and a sign-in button. Don't add a toast underneath it.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return;
      setNotification(`Reload failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }, [initial.id, recordServerItems]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const resetTo = useCallback(
    (a: Agenda) => {
      serverVersionRef.current = a.version;
      recordServerItems(a.items);
      setDraft(a);
      setConflictBannerVisible(false);
    },
    [recordServerItems],
  );

  const setMeta = useCallback(
    (patch: Partial<Agenda>) => {
      if (blockReason) return;
      setDraft((prev) => ({ ...prev, ...patch }));
      scheduleMetaPatch(patch);
    },
    [scheduleMetaPatch, blockReason],
  );

  const updateItem = useCallback(
    (index: number, item: Item) => {
      if (blockReason) return;
      setDraft((prev) => {
        const items = prev.items.slice();
        items[index] = item;
        return { ...prev, items };
      });
      scheduleItemPatch(item.id, item);
    },
    [scheduleItemPatch, blockReason],
  );

  const updateSubitems = useCallback(
    (index: number, subitems: Subitem[]) => {
      if (blockReason) return;
      const baseItem = draft.items[index];
      if (!baseItem) return;
      const updatedItem: Item = { ...baseItem, subitems };
      setDraft((prev) => {
        const items = prev.items.slice();
        items[index] = updatedItem;
        return { ...prev, items };
      });
      scheduleItemPatch(updatedItem.id, updatedItem);
    },
    [draft, scheduleItemPatch, blockReason],
  );

  const addItem = useCallback((): number => {
    if (blockReason) return -1;
    const defaultType = meetingTypeConfig
      ? getDefaultItemType(meetingTypeConfig)
      : 'main';
    const newItem: Item = {
      id: generateUUID(),
      type: defaultType,
      title: 'New item',
      timeRange: { duration: 10 },
    };

    // Insert before the insertion-point sentinel when present.
    const insertionIdx = draft.items.findIndex((i) => i.type === 'insertion-point');
    const newIndex = insertionIdx === -1 ? draft.items.length : insertionIdx;
    const beforeID = insertionIdx !== -1 ? draft.items[insertionIdx].id : null;
    const afterID = insertionIdx === -1 ? (draft.items[draft.items.length - 1]?.id ?? null) : null;

    setDraft((prev) => {
      const items = prev.items.slice();
      items.splice(newIndex, 0, newItem);
      return { ...prev, items };
    });
    setJustAddedItemId(newItem.id);

    void enqueueMutation(async (isTail) => {
      // Read the version at execution time, after the prior queued op committed,
      // so we never send a stale version that triggers a self-conflict 409.
      const version = serverVersionRef.current;
      try {
        const serverAgenda = await api.addItem(initial.id, newItem, afterID, version, beforeID);
        serverVersionRef.current = serverAgenda.version;
        recordServerItems(serverAgenda.items);
        // Tail reconciliation: only the last queued op replaces the draft
        // wholesale; intermediate ops keep the optimistic draft to avoid flicker.
        if (isTail()) setDraft(reconcileWithPending(serverAgenda));
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          await reloadFromServer();
          setConflictBannerVisible(true);
        } else if (e instanceof ApiError && e.status === 404) {
          await reloadFromServer();
        } else {
          // Roll back optimistic insert, then block: the change did not persist.
          setDraft((prev) => ({
            ...prev,
            items: prev.items.filter((item) => item.id !== newItem.id),
          }));
          blockEditing(e);
        }
      }
    });

    return newIndex;
  }, [draft, initial.id, enqueueMutation, recordServerItems, reloadFromServer, reconcileWithPending, meetingTypeConfig, blockEditing, blockReason]);

  const removeItem = useCallback(
    (index: number) => {
      if (blockReason) return;
      const item = draft.items[index];
      if (!item) return;

      setDraft((prev) => {
        const items = prev.items.slice();
        items.splice(index, 1);
        return { ...prev, items };
      });
      setSelectedIndex((cur) => {
        if (cur === null) return cur;
        if (cur === index) return null;
        return cur > index ? cur - 1 : cur;
      });
      cancelItemPatch(item.id);

      void enqueueMutation(async (isTail) => {
        // Read the version at execution time so a prior queued op's commit
        // doesn't leave us sending a stale version.
        const version = serverVersionRef.current;
        try {
          const serverAgenda = await api.deleteItem(initial.id, item.id, version);
          serverVersionRef.current = serverAgenda.version;
          recordServerItems(serverAgenda.items);
          // Tail reconciliation: only the last queued op replaces the draft.
          if (isTail()) setDraft(reconcileWithPending(serverAgenda));
        } catch (e) {
          if (e instanceof ApiError && e.status === 409) {
            await reloadFromServer();
            setConflictBannerVisible(true);
          } else if (e instanceof ApiError && e.status === 404) {
            // Already gone on the server — that's fine, our optimistic remove was correct.
            await reloadFromServer();
          } else {
            // The delete did not persist; block until the user reloads.
            blockEditing(e);
          }
        }
      });
    },
    [
      draft,
      initial.id,
      cancelItemPatch,
      enqueueMutation,
      recordServerItems,
      reloadFromServer,
      reconcileWithPending,
      blockEditing,
      blockReason,
    ],
  );

  const moveItem = useCallback(
    (index: number, destAgendaID: string): Promise<string | null> => {
      if (blockReason) return Promise.resolve(null);
      const item = draft.items[index];
      if (!item) return Promise.resolve(null);

      // Optimistically remove the row from this agenda, mirroring removeItem.
      setDraft((prev) => {
        const items = prev.items.slice();
        items.splice(index, 1);
        return { ...prev, items };
      });
      setSelectedIndex((cur) => {
        if (cur === null) return cur;
        if (cur === index) return null;
        return cur > index ? cur - 1 : cur;
      });
      cancelItemPatch(item.id);

      return enqueueMutation(async (isTail) => {
        // Read the version at execution time so a prior queued op's commit
        // doesn't leave us sending a stale version.
        const version = serverVersionRef.current;
        try {
          const { source, destination } = await api.moveItem(
            initial.id,
            item.id,
            destAgendaID,
            version,
          );
          serverVersionRef.current = source.version;
          recordServerItems(source.items);
          if (isTail()) setDraft(reconcileWithPending(source));
          // The destination changed on the server; seed its cache and drop the
          // list so a later visit shows the moved item rather than a stale copy.
          queryClient.setQueryData(qk.agenda(destAgendaID), destination);
          void queryClient.invalidateQueries({ queryKey: qk.agendas });
          setNotification(`Moved "${item.title || 'item'}" to ${destination.title}`);
          return destination.title;
        } catch (e) {
          if (e instanceof ApiError && e.status === 409) {
            await reloadFromServer();
            setConflictBannerVisible(true);
          } else if (e instanceof ApiError && e.status === 404) {
            await reloadFromServer();
          } else {
            // The move did not persist; block until the user reloads. The item
            // is still on the server (source), so a reload restores it.
            blockEditing(e);
          }
          return null;
        }
      });
    },
    [
      draft,
      initial.id,
      cancelItemPatch,
      enqueueMutation,
      recordServerItems,
      reloadFromServer,
      reconcileWithPending,
      queryClient,
      blockEditing,
      blockReason,
    ],
  );

  const reorder = useCallback(
    (from: number, to: number) => {
      if (blockReason) return;
      // Compute the new order before applying optimistic update.
      const reordered = draft.items.slice();
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      const itemIDs = reordered.map((item) => item.id);

      setDraft((prev) => {
        const items = prev.items.slice();
        const [m] = items.splice(from, 1);
        items.splice(to, 0, m);
        return { ...prev, items };
      });
      setSelectedIndex((cur) => {
        if (cur === null) return cur;
        if (cur === from) return to;
        if (from < cur && to >= cur) return cur - 1;
        if (from > cur && to <= cur) return cur + 1;
        return cur;
      });

      void enqueueMutation(async (isTail) => {
        // Read the version at execution time so a prior queued op's commit
        // doesn't leave us sending a stale version.
        const version = serverVersionRef.current;
        try {
          const serverAgenda = await api.reorderItems(initial.id, itemIDs, version);
          serverVersionRef.current = serverAgenda.version;
          recordServerItems(serverAgenda.items);
          // Tail reconciliation: only the last queued op replaces the draft.
          if (isTail()) setDraft(reconcileWithPending(serverAgenda));
        } catch (e) {
          if (e instanceof ApiError && e.status === 409) {
            await reloadFromServer();
            setConflictBannerVisible(true);
          } else if (e instanceof ApiError && e.status === 404) {
            await reloadFromServer();
          } else {
            // The reorder did not persist; block until the user reloads.
            blockEditing(e);
          }
        }
      });
    },
    [draft, initial.id, enqueueMutation, recordServerItems, reloadFromServer, reconcileWithPending, blockEditing, blockReason],
  );

  // ── Context value ─────────────────────────────────────────────────────────

  const value = useMemo<EditorContextValue>(
    () => ({
      draft,
      saveStatus,
      selectedIndex,
      select: setSelectedIndex,
      setMeta,
      updateItem,
      updateSubitems,
      addItem,
      removeItem,
      moveItem,
      reorder,
      resetTo,
      reloadFromServer,
      conflictBannerVisible,
      dismissConflictBanner: () => setConflictBannerVisible(false),
      blockReason,
      editingBlocked: blockReason !== null,
      notification,
      clearNotification: () => setNotification(null),
      justAddedItemId,
      clearJustAdded: () => setJustAddedItemId(null),
    }),
    [
      draft,
      saveStatus,
      selectedIndex,
      setMeta,
      updateItem,
      updateSubitems,
      addItem,
      removeItem,
      moveItem,
      reorder,
      resetTo,
      reloadFromServer,
      conflictBannerVisible,
      blockReason,
      notification,
      justAddedItemId,
    ],
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}
